import React, { useEffect } from "react";
import { Plus } from "../Icons";

const AddTimeSlotModal = ({
  open,
  allDays,
  form,
  setForm,
  errorMessage,
  isSubmitting,
  onClose,
  onSubmit,
}) => {
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/40 p-4 sm:items-center"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="max-h-[calc(100vh-2rem)] w-full max-w-[627px] overflow-y-auto rounded-2xl bg-white p-4 font-['Inter'] sm:p-6 lg:p-8"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-time-slot-title"
      >
        <div
          id="add-time-slot-title"
          className="flex items-center gap-3 text-xl font-semibold text-black sm:gap-4 sm:text-2xl"
        >
          <Plus />
          Add Time Slot
        </div>

        <div className="mt-5 sm:mt-7">
          <div>
            <label className="text-base font-medium leading-normal text-black sm:text-lg lg:text-[22px]">
              Session Title
            </label>
            <input
              type="text"
              value={form.sessionTitle}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  sessionTitle: event.target.value,
                }))
              }
              className="mt-2 w-full rounded-lg border border-[#D9D9D9] px-3 py-3 text-base sm:py-4"
            />
          </div>

          <div className="mt-4 sm:mt-5">
            <label className="text-base font-medium leading-normal text-black sm:text-lg lg:text-[22px]">
              Schedule Type
            </label>
            <p className="mt-2 rounded-lg border border-[#D9D9D9] bg-[#FAFAFA] px-3 py-3 text-base text-[#4E5566] sm:py-4">
              Recurring Weekly
            </p>
          </div>

          <div className="mt-4 sm:mt-5">
            <label className="text-base font-medium leading-normal text-black sm:text-lg lg:text-[22px]">
              Select day(s)
            </label>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:mt-5 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4 lg:gap-6">
              {allDays.map((day) => {
                const checked = form.days.includes(day);
                return (
                  <label
                    key={day}
                    className="flex items-center gap-2 text-base font-medium leading-normal text-black sm:gap-3 sm:text-lg lg:text-[22px]"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(event) => {
                        setForm((prev) => {
                          const nextDays = event.target.checked
                            ? [...prev.days, day]
                            : prev.days.filter((d) => d !== day);
                          return { ...prev, days: nextDays };
                        });
                      }}
                      className="h-6 w-6 shrink-0 appearance-none rounded border-2 border-[#0D99FF] bg-white checked:border-[#0D99FF] checked:bg-[#0D99FF] checked:bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2020%2016%22%3E%3Cpath%20fill%3D%22%23FFFFFF%22%20d%3D%22M7.5%2013.2L1.9%207.6l1.8-1.8%203.8%203.8%208-8%201.8%201.8z%22/%3E%3C/svg%3E')] checked:bg-no-repeat checked:bg-center checked:bg-[length:14px_11px] sm:h-[30px] sm:w-[30px] sm:checked:bg-[length:18px_14px]"
                    />
                    {day}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:mt-11 sm:grid-cols-2 sm:gap-6">
            <div>
              <label className="text-base font-medium leading-normal text-black sm:text-lg lg:text-[22px]">
                Start Time
              </label>
              <input
                type="time"
                value={form.startTime}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    startTime: event.target.value,
                  }))
                }
                className="mt-2 w-full rounded-lg border border-[#D9D9D9] px-3 py-3 text-base text-black sm:py-[13px] sm:text-xl"
              />
            </div>
            <div>
              <label className="text-base font-medium leading-normal text-black sm:text-lg lg:text-[22px]">
                End Time
              </label>
              <input
                type="time"
                value={form.endTime}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    endTime: event.target.value,
                  }))
                }
                className="mt-2 w-full rounded-lg border border-[#D9D9D9] px-3 py-3 text-base text-black sm:py-[13px] sm:text-xl"
              />
            </div>
          </div>
        </div>

        {errorMessage ? (
          <p className="mt-4 text-sm font-medium text-[#DC2626] sm:mt-5">
            {errorMessage}
          </p>
        ) : null}

        <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end sm:gap-6">
          <button
            type="button"
            className="w-full rounded border border-[#D9D9D9] bg-[#FDFDFD] px-[26px] py-2.5 text-base font-medium text-[#323232] sm:w-auto sm:py-2 sm:text-lg"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="w-full rounded bg-[#089BC4] px-8 py-2.5 text-base font-medium text-white sm:w-auto sm:px-12 sm:py-[9px] sm:text-lg"
            onClick={onSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Adding..." : "Add Time Slot"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTimeSlotModal;
